import { useState, useMemo } from "react";
import {
  Button as AntButton,
  message,
  Modal,
  Space,
  Form,
  Input,
  InputNumber,
  Select,
  Tag,
  DatePicker,
  Popconfirm,
  Tooltip,
  Drawer,
  Table,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  TagsOutlined,
  FileImageOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  fetchOfficeExpenses,
  fetchExpenseStats,
  fetchExpenseCategories,
  createOfficeExpense,
  updateOfficeExpense,
  deleteOfficeExpense,
  createExpenseCategory,
  deleteExpenseCategory,
  fetchBranches,
  uploadMedia,
  fetchDealerPaymentAccounts,
  type CreateOfficeExpenseInput,
} from "../api/auth";
import type { OfficeExpense } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { Metric } from "../components/Metric";
import {
  DataTable,
  ErrorNote,
  PageHeader,
  PageShell,
  Panel,
} from "../components/Page";

const { Option } = Select;
const { RangePicker } = DatePicker;

const PAYMENT_METHODS = [
  { label: "Cash", value: "CASH" },
  { label: "bKash", value: "BKASH" },
  { label: "Nagad", value: "NAGAD" },
  { label: "Bank Transfer", value: "BANK_TRANSFER" },
  { label: "Card / POS", value: "CARD" },
  { label: "Cheque", value: "CHEQUE" },
  { label: "Other", value: "OTHER" },
];

export default function OfficeExpensesPage() {
  const { seller } = useAuth();
  const queryClient = useQueryClient();

  // Filters state
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [selectedBranch, setSelectedBranch] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  // Modals state
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<OfficeExpense | null>(null);
  const [submittingExpense, setSubmittingExpense] = useState(false);
  const [form] = Form.useForm();

  // Receipt preview
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [uploadingVoucher, setUploadingVoucher] = useState(false);
  const [voucherUrl, setVoucherUrl] = useState<string | null>(null);

  // Categories drawer
  const [categoriesDrawerOpen, setCategoriesDrawerOpen] = useState(false);
  const [categoryForm] = Form.useForm();
  const [addingCategory, setAddingCategory] = useState(false);

  // Queries
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["office-expense-stats", selectedBranch],
    queryFn: () => fetchExpenseStats(selectedBranch),
    enabled: !!seller,
  });

  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ["office-expense-categories"],
    queryFn: () => fetchExpenseCategories(),
    enabled: !!seller,
  });

  const { data: branches = [] } = useQuery({
    queryKey: ["seller-branches"],
    queryFn: () => fetchBranches(),
    enabled: !!seller,
  });

  const { data: paymentAccounts = [] } = useQuery({
    queryKey: ["dealer-payment-accounts"],
    queryFn: () => fetchDealerPaymentAccounts(),
    enabled: !!seller,
  });

  const queryParams = useMemo(() => {
    return {
      page,
      limit: 15,
      search: search.trim() || undefined,
      categoryId: selectedCategory || undefined,
      branchId: selectedBranch || undefined,
      fromDate: dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : undefined,
      toDate: dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : undefined,
    };
  }, [page, search, selectedCategory, selectedBranch, dateRange]);

  const {
    data: expensesData,
    isLoading: expensesLoading,
    error: expensesError,
    refetch,
  } = useQuery({
    queryKey: ["office-expenses", queryParams],
    queryFn: () => fetchOfficeExpenses(queryParams),
    enabled: !!seller,
  });

  // Open modal for Create
  const handleOpenCreate = () => {
    setEditingExpense(null);
    setVoucherUrl(null);
    form.resetFields();
    const defaultAcc = paymentAccounts.find((a) => a.isDefault) || paymentAccounts[0];
    form.setFieldsValue({
      expenseDate: dayjs(),
      paymentMethod: defaultAcc ? (defaultAcc.accountType === "BKASH" ? "BKASH" : defaultAcc.accountType === "BANK" ? "BANK_TRANSFER" : "CASH") : "CASH",
      paymentAccountId: defaultAcc?.id,
      currency: "BDT",
    });
    setExpenseModalOpen(true);
  };

  // Open modal for Edit
  const handleOpenEdit = (expense: OfficeExpense) => {
    setEditingExpense(expense);
    setVoucherUrl(expense.receiptUrl || null);
    form.resetFields();
    form.setFieldsValue({
      title: expense.title,
      amount: expense.amount,
      categoryId: expense.categoryId || undefined,
      branchId: expense.branchId || undefined,
      paymentMethod: expense.paymentMethod || undefined,
      paymentAccountId: expense.paymentAccountId || undefined,
      paymentAccount: expense.paymentAccount || undefined,
      expenseDate: expense.expenseDate ? dayjs(expense.expenseDate) : dayjs(),
      reference: expense.reference || undefined,
      notes: expense.notes || undefined,
    });
    setExpenseModalOpen(true);
  };

  // Submit Expense Form
  const handleSubmitExpense = async (values: any) => {
    setSubmittingExpense(true);
    try {
      const selectedAcc = paymentAccounts.find((a) => a.id === values.paymentAccountId);
      const payload: CreateOfficeExpenseInput = {
        title: values.title.trim(),
        amount: Number(values.amount),
        currency: "BDT",
        categoryId: values.categoryId || undefined,
        branchId: values.branchId || undefined,
        paymentMethod: values.paymentMethod || undefined,
        paymentAccountId: values.paymentAccountId || undefined,
        paymentAccount: selectedAcc ? selectedAcc.name : values.paymentAccount?.trim() || undefined,
        expenseDate: values.expenseDate ? values.expenseDate.toISOString() : undefined,
        reference: values.reference?.trim() || undefined,
        receiptUrl: voucherUrl || undefined,
        notes: values.notes?.trim() || undefined,
      };

      if (editingExpense) {
        await updateOfficeExpense(editingExpense.id, payload);
        message.success("Expense updated successfully");
      } else {
        await createOfficeExpense(payload);
        message.success("Expense recorded and debited from payment account");
      }

      setExpenseModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ["office-expenses"] });
      queryClient.invalidateQueries({ queryKey: ["office-expense-stats"] });
      queryClient.invalidateQueries({ queryKey: ["office-expense-categories"] });
      queryClient.invalidateQueries({ queryKey: ["dealer-payment-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["dealer-ledger"] });
      queryClient.invalidateQueries({ queryKey: ["dealer-ledger-stats"] });
    } catch (err: any) {
      message.error(err?.message || "Failed to save office expense");
    } finally {
      setSubmittingExpense(false);
    }
  };

  // Delete Expense
  const handleDeleteExpense = async (id: string) => {
    try {
      await deleteOfficeExpense(id);
      message.success("Expense removed and ledger entry reversed");
      queryClient.invalidateQueries({ queryKey: ["office-expenses"] });
      queryClient.invalidateQueries({ queryKey: ["office-expense-stats"] });
      queryClient.invalidateQueries({ queryKey: ["office-expense-categories"] });
      queryClient.invalidateQueries({ queryKey: ["dealer-payment-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["dealer-ledger"] });
      queryClient.invalidateQueries({ queryKey: ["dealer-ledger-stats"] });
    } catch (err: any) {
      message.error(err?.message || "Failed to delete expense");
    }
  };

  // Handle Voucher File Upload
  const handleVoucherUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingVoucher(true);
    try {
      const asset = await uploadMedia(file);
      setVoucherUrl(asset.url);
      message.success("Voucher uploaded");
    } catch (err: any) {
      message.error(err?.message || "Failed to upload voucher");
    } finally {
      setUploadingVoucher(false);
    }
  };

  // Add Category
  const handleAddCategory = async (values: { name: string; description?: string }) => {
    setAddingCategory(true);
    try {
      await createExpenseCategory({
        name: values.name.trim(),
        description: values.description?.trim(),
      });
      message.success("Category created");
      categoryForm.resetFields();
      queryClient.invalidateQueries({ queryKey: ["office-expense-categories"] });
    } catch (err: any) {
      message.error(err?.message || "Failed to add category");
    } finally {
      setAddingCategory(false);
    }
  };

  // Delete Category
  const handleDeleteCategory = async (id: string) => {
    try {
      await deleteExpenseCategory(id);
      message.success("Category deleted");
      queryClient.invalidateQueries({ queryKey: ["office-expense-categories"] });
    } catch (err: any) {
      message.error(err?.message || "Failed to delete category");
    }
  };

  // Map branches for quick lookup
  const branchMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of branches) {
      map.set(b.id, b.name);
    }
    return map;
  }, [branches]);

  // Table Columns
  const columns: ColumnsType<OfficeExpense> = [
    {
      title: "Date",
      dataIndex: "expenseDate",
      key: "expenseDate",
      width: 110,
      render: (d: string) => (
        <span className="text-xs text-ink-muted">
          {d ? dayjs(d).format("DD MMM YYYY") : "—"}
        </span>
      ),
    },
    {
      title: "Expense Details",
      key: "title",
      render: (_, r) => (
        <div>
          <div className="font-semibold text-ink leading-tight">{r.title}</div>
          {(r.reference || r.notes) && (
            <div className="text-xs text-ink-muted mt-0.5 space-x-1">
              {r.reference && <span className="bg-stone-100 px-1 py-0.5 rounded font-mono">Ref: {r.reference}</span>}
              {r.notes && <span className="italic">{r.notes}</span>}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 160,
      render: (cat: { id: string; name: string } | null) =>
        cat ? (
          <Tag color="cyan" className="font-medium">
            {cat.name}
          </Tag>
        ) : (
          <Tag className="text-ink-muted">General</Tag>
        ),
    },
    {
      title: "Showroom",
      dataIndex: "branchId",
      key: "branchId",
      width: 140,
      render: (bId: string | null) =>
        bId && branchMap.has(bId) ? (
          <Tag className="font-normal text-stone-700 bg-stone-100 border-stone-200">
            {branchMap.get(bId)}
          </Tag>
        ) : (
          <span className="text-xs text-ink-muted">Main Branch</span>
        ),
    },
    {
      title: "Payment Account",
      key: "payment",
      width: 170,
      render: (_, r) => {
        const acc = paymentAccounts.find((a) => a.id === r.paymentAccountId);
        const name = acc?.name || r.paymentAccount || "Cash Drawer";
        return (
          <div className="text-xs">
            <div className="font-semibold text-stone-800 flex items-center gap-1.5">
              <span>{name}</span>
            </div>
            <div className="text-[11px] text-stone-500 mt-0.5">
              {r.paymentMethod || (acc ? acc.accountType : "CASH")}
            </div>
          </div>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 130,
      render: (amt: number) => (
        <span className="font-bold text-rose-600 text-sm">
          ৳{Number(amt || 0).toLocaleString()}
        </span>
      ),
    },
    {
      title: "Voucher",
      dataIndex: "receiptUrl",
      key: "receiptUrl",
      align: "center",
      width: 80,
      render: (url: string | null) =>
        url ? (
          <AntButton
            type="link"
            size="small"
            icon={<FileImageOutlined />}
            onClick={() => setPreviewImage(url)}
            className="text-xs text-accent"
          >
            View
          </AntButton>
        ) : (
          <span className="text-ink-muted text-xs">—</span>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      width: 90,
      render: (_, r) => (
        <Space orientation="horizontal" size="small">
          <Tooltip title="Edit Expense">
            <AntButton
              type="text"
              size="small"
              icon={<EditOutlined className="text-ink-muted hover:text-ink" />}
              onClick={() => handleOpenEdit(r)}
            />
          </Tooltip>
          <Tooltip title="Delete Expense">
            <Popconfirm
              title="Delete Expense"
              description="Are you sure you want to remove this office expense?"
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDeleteExpense(r.id)}
            >
              <AntButton
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <PageShell>
      <PageHeader
        title="Office Expenses"
        description="Track and manage showroom office expenses, utilities, rent, and vouchers"
        actions={
          <Space>
            <AntButton
              icon={<TagsOutlined />}
              onClick={() => setCategoriesDrawerOpen(true)}
              className="border-stone-300 hover:border-stone-400"
            >
              Categories
            </AntButton>
            <AntButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenCreate}
              className="bg-accent hover:bg-accent-hover"
            >
              Add Expense
            </AntButton>
          </Space>
        }
      />

      {/* KPI Stats Row */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric
          label="Total All-Time"
          value={
            statsLoading ? (
              "—"
            ) : (
              `৳${Number(stats?.totalAllTime || 0).toLocaleString()}`
            )
          }
          hint={
            stats?.comparison ? (
              <span
                className={`inline-flex items-center gap-0.5 text-xs font-medium ${
                  stats.comparison.trend === "increase"
                    ? "text-rose-600"
                    : stats.comparison.trend === "decrease"
                    ? "text-emerald-600"
                    : "text-ink-muted"
                }`}
              >
                {stats.comparison.trend === "increase" ? (
                  <ArrowUpOutlined />
                ) : stats.comparison.trend === "decrease" ? (
                  <ArrowDownOutlined />
                ) : null}
                {Math.abs(stats.comparison.percentageChange)}% vs last month
              </span>
            ) : (
              "Recorded expenses"
            )
          }
        />
        <Metric
          label={stats?.currentMonth?.monthName || "This Month"}
          value={
            statsLoading ? (
              "—"
            ) : (
              `৳${Number(stats?.currentMonth?.amount || 0).toLocaleString()}`
            )
          }
          hint={`${stats?.counts?.currentMonth || 0} expenses this month`}
        />
        <Metric
          label={stats?.lastMonth?.monthName || "Last Month"}
          value={
            statsLoading ? (
              "—"
            ) : (
              `৳${Number(stats?.lastMonth?.amount || 0).toLocaleString()}`
            )
          }
          hint={`${stats?.counts?.lastMonth || 0} expenses last month`}
        />
        <Metric
          label="Total Entries"
          value={statsLoading ? "—" : String(stats?.counts?.total || 0)}
          hint={
            stats?.topCategories?.[0]
              ? `Top: ${stats.topCategories[0].name} (৳${stats.topCategories[0].total.toLocaleString()})`
              : "All branches combined"
          }
        />
      </div>

      {/* Filter and Table Panel */}
      <Panel title="Expense Records">
        {/* Filter Controls */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 bg-stone-50 p-3 rounded-lg border border-stone-200">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Search title, ref, or account..."
              prefix={<SearchOutlined className="text-ink-muted" />}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-56"
              allowClear
            />

            <Select
              placeholder="All Categories"
              value={selectedCategory}
              onChange={(val) => {
                setSelectedCategory(val);
                setPage(1);
              }}
              className="w-48"
              allowClear
            >
              {categories.map((c) => (
                <Option key={c.id} value={c.id}>
                  {c.name}
                </Option>
              ))}
            </Select>

            {branches.length > 0 && (
              <Select
                placeholder="All Showrooms"
                value={selectedBranch}
                onChange={(val) => {
                  setSelectedBranch(val);
                  setPage(1);
                }}
                className="w-44"
                allowClear
              >
                {branches.map((b) => (
                  <Option key={b.id} value={b.id}>
                    {b.name}
                  </Option>
                ))}
              </Select>
            )}

            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                setDateRange(dates);
                setPage(1);
              }}
              className="w-64"
            />
          </div>

          <div className="flex items-center gap-2">
            {(search || selectedCategory || selectedBranch || dateRange) && (
              <AntButton
                size="small"
                onClick={() => {
                  setSearch("");
                  setSelectedCategory(undefined);
                  setSelectedBranch(undefined);
                  setDateRange(null);
                  setPage(1);
                }}
              >
                Clear Filters
              </AntButton>
            )}
            <Tooltip title="Refresh">
              <AntButton
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => refetch()}
              />
            </Tooltip>
          </div>
        </div>

        {expensesError ? (
          <ErrorNote error={expensesError} />
        ) : (
          <DataTable<OfficeExpense>
            rowKey="id"
            loading={expensesLoading}
            columns={columns}
            rows={expensesData?.rows || []}
            page={page}
            pageSize={15}
            total={expensesData?.total || 0}
            onPageChange={(p: number) => setPage(p)}
            emptyText="No expenses recorded yet. Click 'Add Expense' above to record rent, utilities, transport, or supplies."
          />
        )}
      </Panel>

      {/* Add / Edit Expense Modal */}
      <Modal
        title={editingExpense ? "Edit Office Expense" : "Record Office Expense"}
        open={expenseModalOpen}
        onCancel={() => setExpenseModalOpen(false)}
        footer={null}
        destroyOnClose
        width={560}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitExpense}
          className="mt-4"
        >
          <Form.Item
            label="Expense Title / Description"
            name="title"
            rules={[{ required: true, message: "Please enter an expense title" }]}
          >
            <Input placeholder="e.g. Showroom Electricity Bill, Staff Lunch, Office Supplies" />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="Amount (BDT)"
              name="amount"
              rules={[{ required: true, message: "Please specify amount" }]}
            >
              <InputNumber
                className="w-full"
                min={0}
                prefix="৳"
                placeholder="0"
              />
            </Form.Item>

            <Form.Item
              label="Expense Date"
              name="expenseDate"
              rules={[{ required: true, message: "Select date" }]}
            >
              <DatePicker className="w-full" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item label="Category" name="categoryId">
              <Select placeholder="Select category" allowClear>
                {categories.map((c) => (
                  <Option key={c.id} value={c.id}>
                    {c.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {branches.length > 0 ? (
              <Form.Item label="Showroom Branch" name="branchId">
                <Select placeholder="Select showroom" allowClear>
                  {branches.map((b) => (
                    <Option key={b.id} value={b.id}>
                      {b.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            ) : (
              <Form.Item label="Showroom Branch">
                <Input disabled value="Main Showroom" />
              </Form.Item>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item label="Payment Method" name="paymentMethod">
              <Select placeholder="Select method">
                {PAYMENT_METHODS.map((m) => (
                  <Option key={m.value} value={m.value}>
                    {m.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Payment Account"
              name="paymentAccountId"
              extra="Expense amount will be deducted from this account's ledger."
            >
              <Select
                placeholder="Select payment account"
                allowClear
                onChange={(accId) => {
                  const acc = paymentAccounts.find((a) => a.id === accId);
                  if (acc) {
                    if (acc.accountType === "CASH") form.setFieldValue("paymentMethod", "CASH");
                    else if (acc.accountType === "BKASH") form.setFieldValue("paymentMethod", "BKASH");
                    else if (acc.accountType === "NAGAD") form.setFieldValue("paymentMethod", "NAGAD");
                    else if (acc.accountType === "BANK") form.setFieldValue("paymentMethod", "BANK_TRANSFER");
                  }
                }}
              >
                {paymentAccounts.map((acc) => (
                  <Option key={acc.id} value={acc.id}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-stone-800">{acc.name}</span>
                      <span className="text-xs text-stone-500 font-mono">
                        ৳{Number(acc.currentBalance).toLocaleString()}
                      </span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <Form.Item label="Reference / Voucher No" name="reference">
            <Input placeholder="e.g. VOUCHER-1029, Cheque #4928" />
          </Form.Item>

          {/* Voucher / Receipt Upload */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-ink mb-1.5">
              Receipt / Voucher Document
            </label>
            {voucherUrl ? (
              <div className="flex items-center gap-3 p-2 border border-stone-200 rounded-lg bg-stone-50">
                <img
                  src={voucherUrl}
                  alt="Voucher receipt preview"
                  className="h-16 w-16 object-cover rounded border border-stone-300"
                />
                <div className="flex-1 text-xs">
                  <div className="font-medium text-stone-800">Receipt attached</div>
                  <AntButton
                    type="link"
                    size="small"
                    onClick={() => setPreviewImage(voucherUrl)}
                    className="p-0 text-accent h-auto"
                  >
                    Preview
                  </AntButton>
                </div>
                <AntButton
                  type="text"
                  danger
                  size="small"
                  onClick={() => setVoucherUrl(null)}
                >
                  Remove
                </AntButton>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-stone-300 rounded-lg bg-stone-50 cursor-pointer hover:bg-stone-100 transition">
                <FileImageOutlined className="text-2xl text-stone-400 mb-1" />
                <span className="text-xs text-ink-muted">
                  {uploadingVoucher ? "Uploading..." : "Click to upload receipt image or photo"}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleVoucherUpload}
                  className="hidden"
                  disabled={uploadingVoucher}
                />
              </label>
            )}
          </div>

          <Form.Item label="Notes / Comments" name="notes">
            <Input.TextArea rows={2} placeholder="Optional notes..." />
          </Form.Item>

          <div className="flex justify-end gap-2 pt-2 border-t border-stone-200">
            <AntButton onClick={() => setExpenseModalOpen(false)}>
              Cancel
            </AntButton>
            <AntButton
              type="primary"
              htmlType="submit"
              loading={submittingExpense}
              className="bg-accent hover:bg-accent-hover"
            >
              {editingExpense ? "Update Expense" : "Record Expense"}
            </AntButton>
          </div>
        </Form>
      </Modal>

      {/* Manage Categories Drawer */}
      <Drawer
        title="Expense Categories"
        open={categoriesDrawerOpen}
        onClose={() => setCategoriesDrawerOpen(false)}
        width={420}
      >
        <div className="space-y-6">
          {/* Add Category Form */}
          <div className="bg-stone-50 p-4 rounded-lg border border-stone-200">
            <h4 className="font-semibold text-sm text-ink mb-3">Add New Category</h4>
            <Form
              form={categoryForm}
              layout="vertical"
              onFinish={handleAddCategory}
            >
              <Form.Item
                label="Category Name"
                name="name"
                rules={[{ required: true, message: "Category name is required" }]}
                className="mb-2"
              >
                <Input placeholder="e.g. Showroom Decor, Bike Transport" />
              </Form.Item>
              <Form.Item
                label="Description"
                name="description"
                className="mb-3"
              >
                <Input placeholder="Brief note on what this covers" />
              </Form.Item>
              <AntButton
                type="primary"
                htmlType="submit"
                loading={addingCategory}
                className="w-full bg-accent hover:bg-accent-hover"
              >
                Create Category
              </AntButton>
            </Form>
          </div>

          {/* Existing Categories List */}
          <div>
            <h4 className="font-semibold text-sm text-ink mb-3">
              Existing Categories ({categories.length})
            </h4>
            <Table
              dataSource={categories}
              rowKey="id"
              loading={categoriesLoading}
              pagination={false}
              size="small"
              columns={[
                {
                  title: "Name",
                  key: "name",
                  render: (_, cat) => (
                    <div>
                      <div className="font-medium text-sm text-stone-900">{cat.name}</div>
                      {cat.description && (
                        <div className="text-xs text-ink-muted">{cat.description}</div>
                      )}
                    </div>
                  ),
                },
                {
                  title: "Expenses",
                  key: "count",
                  width: 80,
                  align: "center",
                  render: (_, cat) => (
                    <Tag color="stone" className="text-xs">
                      {cat._count?.expenses || 0}
                    </Tag>
                  ),
                },
                {
                  title: "",
                  key: "action",
                  width: 50,
                  align: "right",
                  render: (_, cat) => {
                    const count = cat._count?.expenses || 0;
                    return count > 0 ? (
                      <Tooltip title={`${count} active expense(s) use this category`}>
                        <AntButton
                          type="text"
                          size="small"
                          disabled
                          icon={<DeleteOutlined className="text-stone-300" />}
                        />
                      </Tooltip>
                    ) : (
                      <Popconfirm
                        title="Delete category"
                        description="Are you sure you want to delete this category?"
                        onConfirm={() => handleDeleteCategory(cat.id)}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                      >
                        <AntButton
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>
                    );
                  },
                },
              ]}
            />
          </div>
        </div>
      </Drawer>

      {/* Full Receipt Image Preview Modal */}
      <Modal
        open={!!previewImage}
        onCancel={() => setPreviewImage(null)}
        footer={null}
        title="Voucher / Receipt Preview"
        width={600}
      >
        {previewImage && (
          <div className="flex justify-center p-2">
            <img
              src={previewImage}
              alt="Voucher receipt preview full"
              className="max-h-[75vh] w-auto max-w-full rounded object-contain border border-stone-200"
            />
          </div>
        )}
      </Modal>
    </PageShell>
  );
}
